import { useEffect, useRef, useState } from "react";
import { AGENTS } from "../lib/agents";
import { chat as apiChat, getConversation } from "../lib/api";
import type { AgentId, AppConfig, Message } from "../types";

interface Props {
  config: AppConfig;
  agentId: AgentId;
  setAgentId: (id: AgentId) => void;
  convId: string | null;
  setConvId: (id: string | null) => void;
}

export function Chat({ config, agentId, setAgentId, convId, setConvId }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rounds, setRounds] = useState<{ turn: number; tool_calls?: string[] }[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!convId) {
      setMessages([]);
      return;
    }
    (async () => {
      try {
        const r = await getConversation(config, convId);
        setMessages(r.messages.filter((m) => m.role !== "system"));
      } catch (e: any) {
        setErr(e.message);
      }
    })();
  }, [convId, config]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, busy]);

  async function enviar() {
    if (!input.trim() || busy) return;
    setBusy(true);
    setErr(null);
    setRounds([]);
    const pergunta = input;
    setInput("");
    setMessages((prev) => [
      ...prev,
      {
        id: `tmp-${Date.now()}`,
        role: "user",
        content: pergunta,
        created_at: new Date().toISOString(),
      },
    ]);

    try {
      const r = await apiChat(config, {
        agent: agentId,
        message: pergunta,
        conversation_id: convId,
      });
      if (!convId) setConvId(r.conversation_id);
      setRounds(r.rounds || []);
      const full = await getConversation(config, r.conversation_id);
      setMessages(full.messages.filter((m) => m.role !== "system"));
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  function novaConversa() {
    setConvId(null);
    setMessages([]);
    setRounds([]);
  }

  return (
    <div className="chat">
      <div className="chat-toolbar">
        <label>
          Agente:
          <select value={agentId} onChange={(e) => setAgentId(e.target.value as AgentId)}>
            {AGENTS.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nome}
              </option>
            ))}
          </select>
        </label>
        <button onClick={novaConversa} disabled={busy} className="btn-secondary">
          + Nova conversa
        </button>
      </div>

      <div className="messages">
        {messages.length === 0 && (
          <div className="empty">
            <h3>Olá! Selecione um agente e mande uma tarefa.</h3>
            <p className="muted">
              Eu posso ler arquivos, rodar comandos no servidor, buscar em PDFs, consultar o
              SupriCRM, gerar e-mails para fornecedor, analisar datasheets e muito mais. Tudo é
              registrado no log de auditoria.
            </p>
            <div className="suggestions">
              <code>"Resuma os PDFs em /workspace/TESTE-NF/QT26-1822 e me diga se a DI bate com a planilha modelo"</code>
              <code>"Liste os 10 últimos orçamentos com margem &lt; 10%"</code>
              <code>"Gere um e-mail em inglês pro fornecedor IKEY pedindo Proforma do teclado XYZ"</code>
              <code>"Procure no código do SupriCRM por endpoints que tocam em pedido_compra_itens"</code>
            </div>
          </div>
        )}
        {messages.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
        {busy && (
          <div className="msg msg-assistant pulse">
            <div className="role">assistente</div>
            <div className="content">
              <i>raciocinando…</i>
              {rounds.length > 0 && (
                <ul className="round-trace">
                  {rounds.map((r) => (
                    <li key={r.turn}>
                      turn {r.turn}: {(r.tool_calls || []).join(", ") || "resposta final"}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        {err && <div className="error">⚠ {err}</div>}
        <div ref={endRef} />
      </div>

      <div className="composer">
        <textarea
          rows={3}
          value={input}
          placeholder="Pergunte ou peça uma tarefa… (Ctrl+Enter envia)"
          disabled={busy}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) enviar();
          }}
        />
        <button onClick={enviar} disabled={busy || !input.trim()}>
          Enviar
        </button>
      </div>
    </div>
  );
}

function Bubble({ m }: { m: Message }) {
  if (m.role === "tool") {
    let parsed: any = m.content;
    try { parsed = JSON.parse(m.content || ""); } catch { /* keep string */ }
    return (
      <details className="msg msg-tool">
        <summary>
          <code>{m.name}</code>{" "}
          {parsed?.ok === false ? <span className="badge err">erro</span> : <span className="badge ok">ok</span>}
          {parsed?.duration_ms ? <span className="muted"> · {parsed.duration_ms}ms</span> : null}
        </summary>
        <pre>{typeof parsed === "string" ? parsed : JSON.stringify(parsed, null, 2)}</pre>
      </details>
    );
  }
  if (m.role === "assistant" && m.tool_calls) {
    const calls = Array.isArray(m.tool_calls) ? m.tool_calls : [];
    return (
      <div className="msg msg-assistant">
        <div className="role">assistente · ações</div>
        {m.content && <div className="content">{m.content}</div>}
        <ul className="tool-calls">
          {calls.map((c: any, i: number) => (
            <li key={i}>
              <code>{c.function?.name}</code>
              <pre>{c.function?.arguments}</pre>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className={`msg msg-${m.role}`}>
      <div className="role">{m.role === "user" ? "você" : "assistente"}</div>
      <div className="content">{m.content}</div>
    </div>
  );
}
